import { ButtonBase, Grid, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { AccountData, DetailedPeriod, SummaryPeriod } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { usePriceFormatter } from '../../common/usePriceFormatter';
import { SectionType, getVisibleSectionTypes } from '../../orders/dashboards/sectionMeta';
import { SectionIcon } from '../../orders/dashboards/SectionIcon';
import { DateWindow, persistDateWindow } from '../../orders/dashboards/table/dateWindow';

type AnyPeriod = SummaryPeriod | DetailedPeriod;

const getCount = (data: AnyPeriod | undefined, type: SectionType) => ((data as any)?.[type]?.count ?? 0) as number;

type Props = {
  data?: AnyPeriod;
  label: string;
  window?: DateWindow;
};

export const PeriodStats = ({ data, label, window }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const priceFormatter = usePriceFormatter();
  const posthog = usePostHog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const visibleSections = getVisibleSectionTypes(account);

  const handleClick = () => {
    posthog.capture('Analytics top bar clicked', { window: window ?? null });
    persistDateWindow(window ?? null);
    navigate('/dashboard/analytics#activity');
  };

  return (
    <Grid item>
      <ButtonBase
        onClick={handleClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          textAlign: 'start',
          gap: 0.25,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          padding: '12px 18px',
          '&:hover': { backgroundColor: 'mb.ground' },
        }}>
        <Typography display="block" fontSize={13} color="text.secondary">
          {label}
        </Typography>
        <Grid dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {priceFormatter(data?.total_income ?? 0, false, 'h3')}
        </Grid>
        {!isSmallScreen && (
          <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
            {visibleSections.map((s) => {
              const count = getCount(data, s);
              const sectionLabel = t(`analytics.sections.${s}.title`);
              return (
                <Tooltip key={s} title={sectionLabel}>
                  <Grid
                    container
                    item
                    gap={0.25}
                    alignItems="center"
                    aria-label={`${sectionLabel}: ${count}`}
                    sx={{
                      width: 'auto',
                      px: 0.5,
                      color: count > 0 ? 'text.primary' : 'text.secondary',
                      opacity: count > 0 ? 1 : 0.6,
                    }}>
                    <SectionIcon sectionType={s} />
                    <Typography fontSize={13} color="inherit" fontWeight={count > 0 ? 600 : undefined}>
                      {count}
                    </Typography>
                  </Grid>
                </Tooltip>
              );
            })}
          </Grid>
        )}
      </ButtonBase>
    </Grid>
  );
};
